import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Loader2, MoveLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button, buttonVariants } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';

import { useResetPassword } from 'services/auth/hook';
import { encryptText } from 'lib/kms';
import {
  ResetPasswordFormData,
  ResetPasswordValidator,
} from 'lib/validators/reset-password';
import useAuthenticate from 'hooks/useAuthenticate';

export default function ResetPasswordPage() {
  const { state } = useLocation();
  const { t } = useTranslation();

  const { handleLoginResponse, setUserDetails } = useAuthenticate();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutate } = useResetPassword({
    onSuccess: async (data, { password, username }) => {
      try {
        await handleLoginResponse({ res: data, password });
        setUserDetails({
          email: username,
          setLoading: setIsLoading,
          isLogin: true,
        });
      } catch (error) {
        setIsLoading(false);
        toast.error(t('login_failed_message'));
      }
    },
    onError: () => {
      setIsLoading(false);
      toast.error(t('reset_password_failed_message'));
    },
  });

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordValidator),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(values: ResetPasswordFormData) {
    setIsLoading(true);
    const { password } = values;
    try {
      const encryptedPassword = await encryptText(password);
      mutate({
        username: state?.email || '',
        password: encryptedPassword,
        session: state?.session || '',
      });
    } catch (error) {
      setIsLoading(false);
      toast.error(t('failed_verification_send_msg'));
    }
  }

  return (
    <>
      <div className='absolute top-0 left-0 lg:top-4 sm:left-4'>
        <Link
          to={PATH_ROUTES.LOGIN}
          className={buttonVariants({
            variant: 'ghost',
            className: 'rounded-md',
          })}
        >
          <MoveLeft className='w-4 h-4 mr-2' />{' '}
          <span className='block lg:hidden'>{t('back')}</span>
        </Link>
      </div>

      <div className='flex flex-col items-center w-full mx-auto space-y-8 lg:max-w-xl max-lg:my-12'>
        <div>
          <h2 className='text-2xl font-semibold leading-none tracking-tight text-center transition-colors text-primary'>
            {t('reset_password')}
          </h2>
          <p className='mt-2 font-medium leading-none text-center text-primary'>
            {t('reset_password_info')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full px-8 space-y-8 sm:w-2/3'
          >
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor='new-password'>
                    {t('new_password')}
                  </FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        className='pr-10'
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        id='new-password'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        className='absolute right-0 rounded-md -top-1'
                        onClick={() => setShowPassword((prev) => !prev)}
                        data-testid='toggle-password'
                      >
                        {showPassword ? (
                          <Eye className='w-5 h-5 text-primary' />
                        ) : (
                          <EyeOff className='w-5 h-5 text-primary' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor='confirm-new-password'>
                    {t('confirm_new_password')}
                  </FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        className='pr-10'
                        {...field}
                        type={showConfirmPassword ? 'text' : 'password'}
                        id='confirm-new-password'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        className='absolute right-0 rounded-md -top-1'
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        data-testid='toggle-confirm-password'
                      >
                        {showConfirmPassword ? (
                          <Eye className='w-5 h-5 text-primary' />
                        ) : (
                          <EyeOff className='w-5 h-5 text-primary' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-center'>
              <Button
                type='submit'
                className='px-6 font-semibold rounded-full'
                disabled={!form.formState.isValid || isLoading}
              >
                {isLoading && (
                  <Loader2
                    role='progressbar'
                    className='w-4 h-4 mr-2 animate-spin'
                  />
                )}{' '}
                {t('change_password')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
